// AR position + heading stabilisation (2026-10-05, after the iPhone field
// test: "assets wander a bit; calibrate on one asset, wander to another and
// back, and I end up with a different spot"). Pure maths - no DOM, no
// imports - so it runs under plain Node (frontend/tests/arTrack.test.ts).
// The DOM glue is track.ts (motion events, GPS, calibration anchors) and
// heading.ts (compass + gyro).
//
// Why the overlay wandered: every frame was projected from the RAW GPS fix
// plus a fixed east/north calibration offset. Phone GPS error is mostly a
// slowly wandering bias (multipath near buildings: 3-8 m over a few
// minutes), so a fixed offset is right only at the moment it was made, and
// the bias change between "calibrated here" and "came back here" showed up
// one-for-one as a different spot.
//
// What this does instead:
//  * PositionFilter - a 4-state Kalman filter: device position (e, n) and
//    the GPS bias (b_e, b_n) as a first-order Gauss-Markov process. A GPS
//    fix measures position + bias; a calibration measures position alone,
//    which pins the bias. Between fixes the position moves by pedestrian
//    dead reckoning (step length along the heading); while the phone is not
//    walking it does not move at all. The bias estimate decays back to zero
//    with elapsed time AND distance walked, so a calibration fades smoothly
//    back into plain GPS, with the uncertainty growing as it does.
//  * GPS fixes inconsistent with the motion (Mahalanobis gate) are rejected,
//    unless they keep disagreeing, which means the filter is the one that
//    is wrong (re-centre).
//  * StepDetector - steps and a stationary / walking / fidget state from
//    DeviceMotion accelerationIncludingGravity.
//  * HeadingFilter - compass fused with the gyro (DeviceMotion rotationRate
//    projected on the vertical), magnetic-disturbance detection (a compass
//    jump the gyro didn't see; iOS webkitCompassAccuracy worsening).
//  * anchorStatus - the amber bar's words.
//
// Conventions: local ENU metres from a fixed reference (the first fix);
// headings degrees clockwise from true north; times in SECONDS.

const D2R = Math.PI / 180;

export function wrap180(d: number): number {
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

export function norm360(d: number): number {
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

// ---------------------------------------------------------------------------
// Local frame (same equirectangular maths as arMath.localOffset)
// ---------------------------------------------------------------------------

export class LocalFrame {
  readonly lat0: number;
  readonly lon0: number;
  private readonly kE: number;
  private readonly kN: number;

  constructor(lat0: number, lon0: number) {
    this.lat0 = lat0;
    this.lon0 = lon0;
    const la = lat0 * D2R;
    const a = 6378137, e2 = 0.00669437999014;
    const s = Math.sin(la);
    const nRad = a / Math.sqrt(1 - e2 * s * s);
    const mRad = (a * (1 - e2)) / Math.pow(1 - e2 * s * s, 1.5);
    this.kE = D2R * nRad * Math.cos(la);
    this.kN = D2R * mRad;
  }

  toLocal(lat: number, lon: number): [number, number] {
    return [(lon - this.lon0) * this.kE, (lat - this.lat0) * this.kN];
  }

  toLatLon(e: number, n: number): [number, number] {
    return [this.lat0 + n / this.kN, this.lon0 + e / this.kE];
  }
}

// ---------------------------------------------------------------------------
// Position filter
// ---------------------------------------------------------------------------

export type MotionState = "stationary" | "walking" | "fidget" | "nosensor";

export const TRACK = {
  /** share of the reported GPS accuracy (as variance) that is slowly
   * wandering bias rather than fix-to-fix noise. Phone fixes are heavily
   * smoothed by the OS, so most of the error is bias. */
  GPS_BIAS_SHARE: 0.85,
  /** the bias forgets itself over this long ... */
  BIAS_TAU_S: 90,
  /** ... and over this much walking (the multipath geometry changes) */
  BIAS_TAU_M: 40,
  MIN_GPS_ACC_M: 3,
  /** step length error (1 sigma, fraction) and walking-direction error */
  STEP_LENGTH_SIGMA: 0.12,
  STEP_HEADING_SIGMA_DEG: 8,
  DEFAULT_STEP_M: 0.72,
  /** camera visual odometry (PositionFilter.odometry): scale error (camera
   * height / pitch), random error per metre (m^2 / m), heading error */
  VO_SCALE_SIGMA: 0.03,
  VO_Q_PER_M: 0.004,
  VO_HEADING_SIGMA_DEG: 4,
  /** position random walk (m^2 per second) by motion state */
  Q_STATIONARY: 0.006,
  Q_WALKING: 0.02,
  Q_FIDGET: 0.05,
  Q_NOSENSOR: 1.5,
  /** standing still: fix-to-fix GPS noise counts this many times more */
  STATIONARY_R_MULT: 4,
  /** chi-square, 2 dof, 99.9 % */
  GATE_D2: 13.8,
  /** this many rejected fixes in a row (or this long) -> re-centre on GPS */
  MAX_REJECTS: 4,
  MAX_REJECT_S: 10,
};

export interface TrackEstimate {
  e: number;
  n: number;
  /** 1-sigma position uncertainty, metres (the larger axis) */
  sigma: number;
  biasE: number;
  biasN: number;
  held: boolean;
  motion: MotionState;
  t: number;
}

export interface GpsResult {
  accepted: boolean;
  reason?: "held" | "gate" | "first";
  d2?: number;
  recentred?: boolean;
}

type M4 = number[]; // row-major 4x4

function eye4(scale = 1): M4 {
  return [scale, 0, 0, 0, 0, scale, 0, 0, 0, 0, scale, 0, 0, 0, 0, scale];
}

export class PositionFilter {
  /** state: [e, n, bias_e, bias_n] */
  x: number[] = [0, 0, 0, 0];
  P: M4 = eye4(1e4);
  t: number | null = null;
  ready = false;
  held = false;
  motion: MotionState = "nosensor";
  private sigB2 = TRACK.GPS_BIAS_SHARE * 64;
  private walked = 0; // metres since the last predict (decays the bias)
  private rejects = 0;
  private lastAccept = -Infinity;
  /** last fix accuracy (for display) */
  gpsAcc: number | null = null;

  setMotion(m: MotionState, t?: number): void {
    if (t !== undefined) this.predict(t);
    this.motion = m;
  }

  /** advance the covariance (and the bias decay) to time t */
  predict(t: number): void {
    if (this.t === null) {
      this.t = t;
      return;
    }
    const dt = t - this.t;
    if (dt <= 0) return;
    this.t = t;
    const phi = Math.exp(-dt / TRACK.BIAS_TAU_S - this.walked / TRACK.BIAS_TAU_M);
    this.walked = 0;
    const P = this.P;
    // F = diag(1, 1, phi, phi): P <- F P F'
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        const fi = i >= 2 ? phi : 1, fj = j >= 2 ? phi : 1;
        P[i * 4 + j] *= fi * fj;
      }
    }
    this.x[2] *= phi;
    this.x[3] *= phi;
    const qb = (1 - phi * phi) * this.sigB2;
    P[10] += qb;
    P[15] += qb;
    if (!this.held) {
      const q = (this.motion === "stationary" ? TRACK.Q_STATIONARY
        : this.motion === "walking" ? TRACK.Q_WALKING
        : this.motion === "fidget" ? TRACK.Q_FIDGET : TRACK.Q_NOSENSOR) * dt;
      P[0] += q;
      P[5] += q;
    }
  }

  /** a detected step: dead-reckon `length` metres along `headingDeg` */
  step(t: number, headingDeg: number, length = TRACK.DEFAULT_STEP_M, headingSigmaDeg = TRACK.STEP_HEADING_SIGMA_DEG): void {
    this.predict(t);
    if (this.held || !this.ready) return;
    const hr = headingDeg * D2R;
    const ue = Math.sin(hr), un = Math.cos(hr);
    this.x[0] += length * ue;
    this.x[1] += length * un;
    const along = (TRACK.STEP_LENGTH_SIGMA * length) ** 2;
    const cross = (length * Math.tan(headingSigmaDeg * D2R)) ** 2;
    // Q = along * u u' + cross * p p', p = (un, -ue)
    this.P[0] += along * ue * ue + cross * un * un;
    this.P[5] += along * un * un + cross * ue * ue;
    const c = (along - cross) * ue * un;
    this.P[1] += c;
    this.P[4] += c;
    this.walked += length;
  }

  /** Camera visual odometry (voCore.ts, 2026-10-06): the phone moved (de, dn)
   * metres since the last call, measured off the ground in the camera
   * image. Unlike a step it needs no walking direction (sideways and
   * backwards are measured, not assumed) and sees shuffles the step
   * detector misses. The error is mostly a SCALE error (camera height and
   * pitch: TRACK.VO_SCALE_SIGMA of the distance), plus a small random part
   * per metre, plus the heading's error across the track. */
  odometry(t: number, de: number, dn: number, headingSigmaDeg = TRACK.VO_HEADING_SIGMA_DEG): void {
    this.predict(t);
    if (this.held || !this.ready) return;
    const d = Math.hypot(de, dn);
    if (!(d > 0) || !Number.isFinite(d)) return;
    const ue = de / d, un = dn / d;
    this.x[0] += de;
    this.x[1] += dn;
    const along = (TRACK.VO_SCALE_SIGMA * d) ** 2 + TRACK.VO_Q_PER_M * d;
    const cross = (d * Math.tan(headingSigmaDeg * D2R)) ** 2 + TRACK.VO_Q_PER_M * d;
    this.P[0] += along * ue * ue + cross * un * un;
    this.P[5] += along * un * un + cross * ue * ue;
    const c = (along - cross) * ue * un;
    this.P[1] += c;
    this.P[4] += c;
    this.walked += d;
  }

  /** a GPS fix in local metres with its reported accuracy */
  gps(t: number, e: number, n: number, accuracy: number | null): GpsResult {
    const acc = Math.max(accuracy !== null && Number.isFinite(accuracy) ? accuracy : 10, TRACK.MIN_GPS_ACC_M);
    this.gpsAcc = acc;
    this.sigB2 = TRACK.GPS_BIAS_SHARE * acc * acc;
    let rw = (1 - TRACK.GPS_BIAS_SHARE) * acc * acc;
    if (!this.ready) {
      this.t = t;
      this.x = [e, n, 0, 0];
      // position = fix - bias: the bias is the uncertainty
      const vb = this.sigB2;
      this.P = [vb + rw, 0, -vb, 0, 0, vb + rw, 0, -vb, -vb, 0, vb, 0, 0, -vb, 0, vb];
      this.ready = true;
      this.lastAccept = t;
      return { accepted: true, reason: "first" };
    }
    this.predict(t);
    if (this.held) return { accepted: false, reason: "held" };
    if (this.motion === "stationary") rw *= TRACK.STATIONARY_R_MULT;
    const d2 = this.gpsD2(e, n, rw);
    let recentred = false;
    if (d2 > TRACK.GATE_D2) {
      this.rejects++;
      if (this.rejects < TRACK.MAX_REJECTS && t - this.lastAccept < TRACK.MAX_REJECT_S) {
        return { accepted: false, reason: "gate", d2 };
      }
      // GPS keeps disagreeing: the filter is the one that's wrong
      const P = this.P;
      for (let k = 0; k < 16; k++) if (k % 5 !== 0 && (Math.floor(k / 4) < 2) !== (k % 4 < 2)) P[k] = 0;
      P[0] += acc * acc;
      P[5] += acc * acc;
      P[10] = Math.max(P[10], this.sigB2);
      P[15] = Math.max(P[15], this.sigB2);
      recentred = true;
    }
    // sequential scalar updates (R diagonal): H = [1 0 1 0], [0 1 0 1]
    this.scalarUpdate([1, 0, 1, 0], e, rw);
    this.scalarUpdate([0, 1, 0, 1], n, rw);
    this.rejects = 0;
    this.lastAccept = t;
    return { accepted: true, d2, recentred };
  }

  private gpsD2(e: number, n: number, rw: number): number {
    const P = this.P;
    // S = H P H' + rw I, H = [[1,0,1,0],[0,1,0,1]]
    const s00 = P[0] + P[2] + P[8] + P[10] + rw;
    const s11 = P[5] + P[7] + P[13] + P[15] + rw;
    const s01 = P[1] + P[3] + P[9] + P[11];
    const ye = e - this.x[0] - this.x[2], yn = n - this.x[1] - this.x[3];
    const det = s00 * s11 - s01 * s01;
    if (det <= 0) return 0;
    return (ye * ye * s11 - 2 * ye * yn * s01 + yn * yn * s00) / det;
  }

  /** z = h . x + noise(r) */
  private scalarUpdate(hv: number[], z: number, r: number): void {
    const P = this.P;
    const Ph = [0, 0, 0, 0];
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) Ph[i] += P[i * 4 + j] * hv[j];
    let s = r;
    for (let i = 0; i < 4; i++) s += hv[i] * Ph[i];
    if (s <= 0) return;
    let y = z;
    for (let i = 0; i < 4; i++) y -= hv[i] * this.x[i];
    const K = Ph.map((v) => v / s);
    for (let i = 0; i < 4; i++) this.x[i] += K[i] * y;
    for (let i = 0; i < 4; i++) for (let j = 0; j < 4; j++) P[i * 4 + j] -= K[i] * Ph[j];
    // keep it symmetric
    for (let i = 0; i < 4; i++) for (let j = i + 1; j < 4; j++) {
      const m = (P[i * 4 + j] + P[j * 4 + i]) / 2;
      P[i * 4 + j] = P[j * 4 + i] = m;
    }
  }

  /** A calibration: "right now the device is truly at (e, n)", to within
   * sigma metres. Pins the position and, through the correlation, the GPS
   * bias. */
  anchor(t: number, e: number, n: number, sigma: number): void {
    this.predict(t);
    if (!this.ready) return;
    const r = Math.max(sigma, 0.1) ** 2;
    this.scalarUpdate([1, 0, 0, 0], e, r);
    this.scalarUpdate([0, 1, 0, 0], n, r);
  }

  /** A calibration expressed as "move the current estimate by (de, dn)". */
  anchorShift(t: number, de: number, dn: number, sigma: number): void {
    this.predict(t);
    this.anchor(t, this.x[0] + de, this.x[1] + dn, sigma);
  }

  /** A one-directional calibration (the kerb: across the road only): move
   * the estimate `shift` metres along the unit vector (ue, un). */
  anchorAlong(t: number, ue: number, un: number, shift: number, sigma: number): void {
    this.predict(t);
    if (!this.ready) return;
    const L = Math.hypot(ue, un) || 1;
    const u = [ue / L, un / L, 0, 0];
    const z = u[0] * this.x[0] + u[1] * this.x[1] + shift;
    this.scalarUpdate(u, z, Math.max(sigma, 0.1) ** 2);
  }

  setHold(on: boolean, t: number): void {
    this.predict(t);
    this.held = on;
  }

  estimate(t?: number): TrackEstimate {
    if (t !== undefined) this.predict(t);
    return {
      e: this.x[0], n: this.x[1], sigma: Math.sqrt(Math.max(this.P[0], this.P[5], 0)),
      biasE: this.x[2], biasN: this.x[3], held: this.held, motion: this.motion, t: this.t ?? 0,
    };
  }
}

// ---------------------------------------------------------------------------
// Steps and the stationary detector
// ---------------------------------------------------------------------------

export const STEPS = {
  GRAVITY_TAU_S: 0.6,
  SMOOTH_TAU_S: 0.05,
  /** vertical acceleration (m/s^2, smoothed) for a step peak / trough */
  PEAK: 0.75,
  TROUGH: -0.35,
  MIN_INTERVAL_S: 0.3,
  /** a step within this long = walking */
  WALKING_HOLD_S: 1.6,
  /** rms of the gravity-free acceleration below this (1.2 s window) = still */
  STILL_RMS: 0.25,
  WINDOW_S: 1.2,
};

export class StepDetector {
  private g: [number, number, number] | null = null;
  private lp = 0;
  private lastT: number | null = null;
  private above = false;
  private troughSeen = true;
  lastStepT = -Infinity;
  steps = 0;
  private win: [number, number][] = [];
  private sum2 = 0;
  state: MotionState = "stationary";

  /** accelerationIncludingGravity in m/s^2 (device frame). */
  feed(t: number, ax: number, ay: number, az: number): { step: boolean; state: MotionState } {
    if (![ax, ay, az].every(Number.isFinite)) return { step: false, state: this.state };
    const dt = this.lastT === null ? 0.016 : Math.max(0, Math.min(0.2, t - this.lastT));
    this.lastT = t;
    if (!this.g) this.g = [ax, ay, az];
    const ag = dt / (STEPS.GRAVITY_TAU_S + dt);
    const g = this.g;
    g[0] += ag * (ax - g[0]);
    g[1] += ag * (ay - g[1]);
    g[2] += ag * (az - g[2]);
    const gl = Math.hypot(g[0], g[1], g[2]) || 1;
    const lx = ax - g[0], ly = ay - g[1], lz = az - g[2];
    const vert = (lx * g[0] + ly * g[1] + lz * g[2]) / gl;
    this.lp += (dt / (STEPS.SMOOTH_TAU_S + dt)) * (vert - this.lp);
    let step = false;
    if (this.lp < STEPS.TROUGH) this.troughSeen = true;
    if (this.lp > STEPS.PEAK) this.above = true;
    else if (this.above && this.lp < 0) {
      this.above = false;
      if (this.troughSeen && t - this.lastStepT >= STEPS.MIN_INTERVAL_S) {
        step = true;
        this.steps++;
        this.lastStepT = t;
        this.troughSeen = false;
      }
    }
    const m2 = lx * lx + ly * ly + lz * lz;
    this.win.push([t, m2]);
    this.sum2 += m2;
    while (this.win.length && t - this.win[0][0] > STEPS.WINDOW_S) this.sum2 -= this.win.shift()![1];
    const rms = Math.sqrt(Math.max(this.sum2, 0) / Math.max(this.win.length, 1));
    this.state = t - this.lastStepT < STEPS.WALKING_HOLD_S ? "walking" : rms < STEPS.STILL_RMS ? "stationary" : "fidget";
    return { step, state: this.state };
  }
}

// ---------------------------------------------------------------------------
// Heading: compass + gyro
// ---------------------------------------------------------------------------

/** Rate of change of the compass heading (deg/s, clockwise) from W3C
 * DeviceMotion rotationRate (alpha: about the screen normal z, beta: about
 * x, gamma: about y; deg/s) and the device's beta/gamma: the angular
 * velocity projected on the vertical, sign flipped (counter-clockwise seen
 * from above is a heading DECREASE). */
export function headingRate(rateAlpha: number, rateBeta: number, rateGamma: number, beta: number, gamma: number): number {
  const b = beta * D2R, g = gamma * D2R;
  // world "up" in device coordinates: Ry(g)' Rx(b)' [0,0,1]
  const ux = -Math.sin(g) * Math.cos(b), uy = Math.sin(b), uz = Math.cos(g) * Math.cos(b);
  return -(rateBeta * ux + rateGamma * uy + rateAlpha * uz);
}

export const HEADING = {
  /** the compass pulls the gyro-propagated heading in with this time constant */
  COMPASS_TAU_S: 1.5,
  /** ... slower when iOS says the compass is poor */
  POOR_COMPASS_TAU_S: 5,
  /** no usable gyro: plain circular low-pass */
  LOWPASS_TAU_S: 0.35,
  /** disturbed when compass and gyro disagree by this much ... */
  DISAGREE_DEG: 15,
  /** ... for this long, or jump apart by JUMP_DEG between two readings */
  DISAGREE_S: 0.4,
  JUMP_DEG: 10,
  /** back to normal after agreeing within CALM_DEG for CALM_S */
  CALM_DEG: 5,
  CALM_S: 1.5,
  /** still disagreeing after this long: believe the compass again */
  RESYNC_S: 20,
  /** iOS webkitCompassAccuracy: poor above this, or this much worse than the best seen lately */
  POOR_ACC_DEG: 25,
  ACC_WORSE_DEG: 10,
  ACC_WINDOW_S: 30,
  /** a compass reading after a gap pulls at most like one this far after the last */
  MAX_PULL_DT_S: 0.25,
};

export interface HeadingState {
  heading: number | null;
  disturbed: boolean;
  /** why ("compass disturbed: move away from cars / metal") */
  note: string | null;
  gyro: "unknown" | "ok";
  gain: number;
}

export class HeadingFilter {
  h: number | null = null;
  private lastC: number | null = null;
  private lastCT = 0;
  private gyroSince = 0; // raw gyro heading change since the last compass reading
  private lastGyroT: number | null = null;
  gyroState: "unknown" | "ok" = "unknown";
  gain = 1;
  private winG = 0;
  private winC = 0;
  private winT: number | null = null;
  private sCG = 0;
  private sGG = 0;
  private sCC = 0;
  private nWin = 0;
  disturbed = false;
  private disagreeSince: number | null = null;
  /** compass - heading, low-passed (0.3 s): the disturbed / calm tests use
   * this so compass noise alone never flips them */
  private innovLp = 0;
  private calmSince: number | null = null;
  private disturbedSince = 0;
  poorAccuracy = false;
  private accHist: [number, number][] = [];
  note: string | null = null;

  /** set by asOffset(): the filtered quantity is a slowly varying offset
   * (OrientationFusion: compass north minus the attitude frame's north), not
   * a heading the gyro turns - no gyro, no gain learning */
  private fixedGain = false;

  /** Filter a near-constant OFFSET: the compass samples are offsets, there
   * is no gyro (it is constant between them), and the disturbance logic
   * works on jumps of the offset. */
  asOffset(): this {
    this.fixedGain = true;
    this.gyroState = "ok";
    this.gain = 1;
    return this;
  }

  /** Snap to `c` (a trusted reading after none was trusted). */
  reset(t: number, c: number): void {
    this.h = norm360(c);
    this.lastC = c;
    this.lastCT = t;
    this.winT = t;
    this.gyroSince = 0;
    this.innovLp = 0;
    this.disturbed = false;
    this.disagreeSince = null;
    this.calmSince = null;
  }

  /** rotationRate (deg/s) + the current beta/gamma */
  gyro(t: number, rateAlpha: number, rateBeta: number, rateGamma: number, beta: number, gamma: number): void {
    if (this.fixedGain) return;
    if (![rateAlpha, rateBeta, rateGamma, beta, gamma].every(Number.isFinite)) return;
    const dt = this.lastGyroT === null ? 0 : Math.max(0, Math.min(0.1, t - this.lastGyroT));
    this.lastGyroT = t;
    const dh = headingRate(rateAlpha, rateBeta, rateGamma, beta, gamma) * dt;
    this.gyroSince += dh;
    this.winG += dh;
    if (this.gyroState === "ok" && this.h !== null) this.h = norm360(this.h + this.gain * dh);
  }

  /** A compass reading (true heading) and its reported accuracy (iOS). */
  compass(t: number, c: number, accuracy: number | null = null): HeadingState {
    if (!Number.isFinite(c)) return this.state();
    this.trackAccuracy(t, accuracy);
    if (this.h === null || this.lastC === null) {
      this.h = norm360(c);
      this.lastC = c;
      this.lastCT = t;
      this.winT = t;
      this.gyroSince = 0;
      return this.state();
    }
    const dtc = Math.max(0, t - this.lastCT);
    const dC = wrap180(c - this.lastC);
    const dG = this.gyroSince;
    this.learnGain(t, dC);
    if (this.gyroState === "ok") {
      const innov = wrap180(c - this.h);
      this.innovLp += (1 - Math.exp(-Math.max(dtc, 0.001) / 0.3)) * (innov - this.innovLp);
      const sm = Math.abs(this.innovLp);
      // a sudden step between two readings that the gyro didn't see, and that
      // is still there once smoothed (one noisy sample is not a jump)
      const jump = dtc <= 0.6 && Math.abs(dC - this.gain * dG) > HEADING.JUMP_DEG && sm > 8;
      if (!this.disturbed) {
        if (sm > HEADING.DISAGREE_DEG) this.disagreeSince ??= t;
        else this.disagreeSince = null;
        if (jump || (this.disagreeSince !== null && t - this.disagreeSince >= HEADING.DISAGREE_S)) {
          this.disturbed = true;
          this.disturbedSince = t;
          this.calmSince = null;
        }
      } else {
        if (sm < HEADING.CALM_DEG) {
          this.calmSince ??= t;
          if (t - this.calmSince >= HEADING.CALM_S) this.disturbed = false;
        } else this.calmSince = null;
        if (this.disturbed && t - this.disturbedSince >= HEADING.RESYNC_S) {
          // it has stayed different for a long time: the compass is right (the
          // gyro has drifted, or the disturbance was at the start)
          this.h = norm360(c);
          this.innovLp = 0;
          this.disturbed = false;
        }
      }
      if (!this.disturbed) {
        const tau = this.poorAccuracy ? HEADING.POOR_COMPASS_TAU_S : HEADING.COMPASS_TAU_S;
        // soft gate: a large disagreement pulls in more gently
        const soft = Math.abs(innov) > 8 ? 0.25 : 1;
        // one reading after a long gap (the compass was not trusted for a
        // while) pulls like one reading, not like the whole gap
        this.h = norm360(this.h + soft * (1 - Math.exp(-Math.min(dtc, HEADING.MAX_PULL_DT_S) / tau)) * innov);
      }
    } else {
      this.h = norm360(this.h + (1 - Math.exp(-Math.max(dtc, 0.001) / HEADING.LOWPASS_TAU_S)) * wrap180(c - this.h));
    }
    this.lastC = c;
    this.lastCT = t;
    this.gyroSince = 0;
    return this.state();
  }

  private learnGain(t: number, dC: number): void {
    if (this.fixedGain) return;
    this.winC += dC;
    if (this.winT === null) this.winT = t;
    if (t - this.winT < 0.5) return;
    // the compass turned (units-free test: the gyro may be deg/s or rad/s)
    if (Math.abs(this.winC) > 4 && Math.abs(this.winG) > 1e-3 && !this.disturbed) {
      this.sCG = 0.95 * this.sCG + this.winC * this.winG;
      this.sGG = 0.95 * this.sGG + this.winG * this.winG;
      this.sCC = 0.95 * this.sCC + this.winC * this.winC;
      this.nWin++;
      const corr = this.sCG / Math.sqrt(this.sGG * this.sCC || 1);
      if (this.nWin >= 3 && this.sCC > 150 && Math.abs(corr) > 0.9) {
        const g = this.sCG / this.sGG;
        const a = Math.abs(g);
        // deg/s as the spec says; or rad/s (old Chrome) ~57x; either sign
        if ((a >= 0.6 && a <= 1.6) || (a >= 34 && a <= 92)) {
          this.gain = g;
          this.gyroState = "ok";
        }
      }
    }
    this.winC = 0;
    this.winG = 0;
    this.winT = t;
  }

  private trackAccuracy(t: number, acc: number | null): void {
    if (acc === null || !Number.isFinite(acc) || acc < 0) {
      this.poorAccuracy = false;
      return;
    }
    this.accHist.push([t, acc]);
    while (this.accHist.length && t - this.accHist[0][0] > HEADING.ACC_WINDOW_S) this.accHist.shift();
    const best = Math.min(...this.accHist.map((a) => a[1]));
    this.poorAccuracy = acc >= HEADING.POOR_ACC_DEG || acc - best >= HEADING.ACC_WORSE_DEG;
  }

  state(): HeadingState {
    const disturbed = this.disturbed || this.poorAccuracy;
    this.note = this.disturbed
      ? "Compass disturbed: move away from cars / metal (holding direction on the gyro)"
      : this.poorAccuracy ? "Compass accuracy dropped: move away from cars / metal, or wave the phone in a figure 8" : null;
    return { heading: this.h, disturbed, note: this.note, gyro: this.gyroState, gain: this.gain };
  }
}

// ---------------------------------------------------------------------------
// Device orientation -> the REAR CAMERA's heading, for any pitch and roll
// ---------------------------------------------------------------------------
//
// 2026-10-05, iPhone report: "calibrating with a hydrant: position is
// corrected but the orientation goes all over the show - maybe because I'm
// angling the phone to look around?"
//
// Root cause. heading.ts published iOS `webkitCompassHeading` (+ declination
// + screen angle) AS the camera heading, and the overlay then built the
// camera basis from it plus beta/gamma (cameraBasis assumes the heading is
// the azimuth of the camera's -z axis). But webkitCompassHeading is Core
// Location's heading, referenced to the device's TOP EDGE (portrait): the
// azimuth of the device y axis projected on the horizontal. That is the
// camera's azimuth only while the phone leans back (beta 0-90) with no roll.
// Held up for AR the y axis points nearly straight UP, so its horizontal
// projection is tiny and a few degrees of roll swing it tens of degrees;
// tilted past vertical to look up it flips 180 degrees; rolled, it differs
// from the camera by ~roll / sin(beta). (Some iOS versions switch Core
// Location to the back-camera direction when the device is upright - with
// a switching zone in between. Either way the published value was
// ill-defined exactly in the AR pose.) The gyro fusion then kept being
// pulled to those samples, learned its gain from them, and re-synced to
// them after 20 s. W3C Euler angles are also in gimbal lock at beta = 90
// (alpha and gamma trade off), so nothing taken from single angles is
// stable there - only the full rotation matrix is.
//
// Fix. Work with the rotation matrix of the full orientation
// (alpha, beta, gamma). The camera heading is the azimuth of R * (0,0,-1):
// exact for any pitch and roll (undefined only when the camera looks
// straight up or down), and continuous through the beta = 90 gimbal lock.
// On iOS alpha is RELATIVE (an arbitrary but fixed north, kept by Core
// Motion's gyro), so heading = cameraAzimuth(R_rel) + offset, where offset
// = true north minus the attitude frame's north - one number, the same in
// every pose. webkitCompassHeading only feeds that offset, and only when
// it is trustworthy: the top edge and the camera point the same way
// (|azimuth difference| <= TRUST_MAX_DIFF_DEG, both well off vertical -
// beta ~15-75 deg, little roll: holding the phone to look at the ground
// ahead), so whichever reference iOS uses, the sample is right. In between
// the offset holds (the attitude itself carries the turns). Until the first
// trusted sample, the old assumption is used provisionally. Android: the
// absolute event's camera azimuth is already right in every pose; with the
// relative event as the attitude it feeds the same offset filter.
//
// The offset goes through HeadingFilter.asOffset(): magnetic disturbances
// (an offset that jumps with no rotation) are held off and reported as
// before.

export type M3 = number[]; // row-major 3x3, device frame -> earth frame (x east, y north, z up)

/** W3C DeviceOrientation (intrinsic Z-X'-Y'') as a rotation matrix. */
export function rotationFromEuler(alpha: number, beta: number, gamma: number): M3 {
  const a = alpha * D2R, b = beta * D2R, g = gamma * D2R;
  const ca = Math.cos(a), sa = Math.sin(a), cb = Math.cos(b), sb = Math.sin(b), cg = Math.cos(g), sg = Math.sin(g);
  return [
    ca * cg - sa * sb * sg, -sa * cb, ca * sg + sa * sb * cg,
    sa * cg + ca * sb * sg, ca * cb, sa * sg - ca * sb * cg,
    -cb * sg, sb, cb * cg,
  ];
}

/** The W3C angles of a rotation (beta in [-180, 180), gamma in [-90, 90)), as a
 * browser decomposes it. Near beta = +-90 (gimbal lock) alpha and gamma are
 * ill-defined: the split jumps with tiny changes, as on a real phone. */
export function eulerFromRotation(R: M3): { alpha: number; beta: number; gamma: number } {
  const r20 = R[6], r21 = R[7], r22 = R[8];
  let gamma: number, cb: number;
  if (Math.abs(r22) > 1e-9) {
    gamma = Math.atan(-r20 / r22) / D2R; // cos(gamma) > 0, so sign(cos beta) = sign(r22)
    cb = r22 / Math.cos(gamma * D2R);
  } else if (Math.abs(r20) > 1e-9) {
    gamma = -90;
    cb = r20;
  } else {
    gamma = 0;
    cb = 0;
  }
  const beta = Math.atan2(r21, cb) / D2R;
  // exact gimbal lock: gamma taken as 0, the whole yaw in alpha
  const alpha = Math.abs(cb) > 1e-9 ? Math.atan2(-R[1] / cb, R[4] / cb) / D2R : Math.atan2(R[3], R[0]) / D2R;
  return { alpha: norm360(alpha), beta: beta >= 180 ? beta - 360 : beta, gamma };
}

export function mul3(A: M3, B: M3): M3 {
  const out = new Array(9).fill(0);
  for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) out[i * 3 + j] += A[i * 3 + k] * B[k * 3 + j];
  return out;
}

/** Earth rotation about the vertical, COUNTER-clockwise seen from above by `deg` (the W3C alpha sense). */
export function rotZ3(deg: number): M3 {
  const c = Math.cos(deg * D2R), s = Math.sin(deg * D2R);
  return [c, -s, 0, s, c, 0, 0, 0, 1];
}

function rotX3(deg: number): M3 {
  const c = Math.cos(deg * D2R), s = Math.sin(deg * D2R);
  return [1, 0, 0, 0, c, -s, 0, s, c];
}

/** A portrait phone pose: camera heading `yaw` (clockwise from north),
 * camera `pitch` above the horizon, `roll` about the camera axis (positive:
 * the top of the phone tips to the right as seen by the user). Tests and
 * documentation; R = Rz(-yaw) Rx(90 + pitch) Rz(-roll). */
export function poseMatrix(yaw: number, pitch: number, roll: number): M3 {
  return mul3(mul3(rotZ3(-yaw), rotX3(90 + pitch)), rotZ3(-roll));
}

/** Azimuth (true or frame-relative, as R is) of a device-frame axis, and
 * how horizontal it is (1 = level, 0 = vertical). */
export function axisAzimuth(R: M3, v: [number, number, number]): { azimuth: number | null; horiz: number } {
  const e = R[0] * v[0] + R[1] * v[1] + R[2] * v[2];
  const n = R[3] * v[0] + R[4] * v[1] + R[5] * v[2];
  const horiz = Math.hypot(e, n);
  return { azimuth: horiz < 1e-9 ? null : norm360(Math.atan2(e, n) / D2R), horiz };
}

const CAM_AXIS: [number, number, number] = [0, 0, -1];
const TOP_AXIS: [number, number, number] = [0, 1, 0];

/** The rear camera's azimuth: R * (0,0,-1) on the horizontal (any pitch / roll). */
export function cameraAzimuth(R: M3): { azimuth: number | null; horiz: number } {
  return axisAzimuth(R, CAM_AXIS);
}

/** Camera pitch above the horizon and roll (deg), screen-orientation aware (display only). */
export function cameraPitchRoll(R: M3, screenAngle = 0): { pitch: number; roll: number } {
  const a = screenAngle * D2R;
  const fz = -R[8];
  const upZ = R[6] * Math.sin(a) + R[7] * Math.cos(a);
  const rightZ = R[6] * Math.cos(a) - R[7] * Math.sin(a);
  return { pitch: Math.asin(Math.max(-1, Math.min(1, fz))) / D2R, roll: Math.atan2(rightZ, upZ) / D2R };
}

export const FUSION = {
  /** webkitCompassHeading is trusted only when the top edge and the camera
   * are both at least this far off vertical (horizontal component) ... */
  TRUST_MIN_HORIZ: 0.25,
  /** ... and point within this many degrees of the same azimuth */
  TRUST_MAX_DIFF_DEG: 5,
  /** iOS webkitCompassAccuracy worse than this: not used */
  MAX_ACC_DEG: 45,
  /** Android absolute camera azimuth: the camera at least this far off vertical */
  ABS_MIN_HORIZ: 0.2,
  /** an absolute sample pairs with an attitude reading at most this old */
  REL_STALE_S: 0.25,
  /** no attitude event for this long: the frame may have been reset (page hidden) */
  FRAME_GAP_S: 1.0,
  /** WebkitModel: forgetting per sample (~16 s at 60 Hz), samples / roll
   * variance (deg^2) before deciding, slope tolerance, and how far the top
   * edge and camera may differ for a learned model's samples */
  MODEL_FORGET: 0.999,
  MODEL_MIN_N: 120,
  MODEL_MIN_VAR: 3,
  MODEL_TOL: 0.3,
  MODEL_MAX_DIFF_DEG: 25,
};

export interface WebkitTrust {
  /** top edge and camera both well off vertical and within TRUST_MAX_DIFF_DEG */
  ok: boolean;
  top: number | null;
  cam: number | null;
  topHoriz: number;
  camHoriz: number;
  /** signed top - camera azimuth, deg (null when either is near vertical) */
  diff: number | null;
  why: string | null;
}

/** Is a webkitCompassHeading sample usable in this pose? (frame-independent) */
export function webkitTrust(R: M3): WebkitTrust {
  const top = axisAzimuth(R, TOP_AXIS), cam = cameraAzimuth(R);
  const base = { top: top.azimuth, cam: cam.azimuth, topHoriz: top.horiz, camHoriz: cam.horiz };
  if (top.azimuth === null || cam.azimuth === null || top.horiz < FUSION.TRUST_MIN_HORIZ || cam.horiz < FUSION.TRUST_MIN_HORIZ) {
    const upright = top.horiz < FUSION.TRUST_MIN_HORIZ;
    return { ...base, ok: false, diff: null,
      why: upright ? "phone upright: the iOS compass is unreliable here, direction held on the gyro" : "camera pointing at the ground: direction held on the gyro" };
  }
  const diff = wrap180(top.azimuth - cam.azimuth);
  if (Math.abs(diff) > FUSION.TRUST_MAX_DIFF_DEG) {
    return { ...base, ok: false, diff,
      why: Math.abs(diff) > 90 ? "tilted past vertical: direction held on the gyro" : "phone rolled: direction held on the gyro" };
  }
  return { ...base, ok: true, diff, why: null };
}

/** Which axis does this phone's webkitCompassHeading follow? Learned from
 * the samples themselves: with the attitude carrying the yaw, the offset
 * implied by "compass = top edge" is constant as the hand rolls the phone
 * if that is true (slope 0 against the top - camera difference), and moves
 * one-for-one with the difference if the compass follows the camera
 * (slope -1). Exponentially weighted least squares. */
export class WebkitModel {
  private w = 0;
  private sx = 0;
  private sy = 0;
  private sxx = 0;
  private sxy = 0;
  model: "top" | "camera" | null = null;
  slope: number | null = null;

  add(diff: number, resid: number): void {
    const l = FUSION.MODEL_FORGET;
    this.w = l * this.w + 1;
    this.sx = l * this.sx + diff;
    this.sy = l * this.sy + resid;
    this.sxx = l * this.sxx + diff * diff;
    this.sxy = l * this.sxy + diff * resid;
    const mx = this.sx / this.w, my = this.sy / this.w;
    const vx = this.sxx / this.w - mx * mx;
    if (this.w < FUSION.MODEL_MIN_N || vx < FUSION.MODEL_MIN_VAR) return;
    this.slope = (this.sxy / this.w - mx * my) / vx;
    if (Math.abs(this.slope) < FUSION.MODEL_TOL) this.model = "top";
    else if (Math.abs(this.slope + 1) < FUSION.MODEL_TOL) this.model = "camera";
    else this.model = null;
  }
}

export type CompassRef = "none" | "provisional" | "locked" | "absolute";

export interface SensorDetails {
  /** what is in use ("iOS compass + attitude", "Android compass + attitude", ...) */
  source: string;
  /** what carries the heading between compass readings */
  yaw: "attitude" | "gyro" | "none";
  /** the compass reference: provisional (not yet seen in a trustworthy pose), locked (iOS), absolute (Android) */
  ref: CompassRef;
  /** was the last compass reading used? and if not, why */
  used: boolean;
  why: string | null;
  /** the compass on its own, as a camera heading (true deg), and its accuracy (iOS) */
  compass: number | null;
  accuracy: number | null;
  /** camera pitch above the horizon / roll, deg */
  pitch: number | null;
  roll: number | null;
  /** seconds since a compass reading was last used */
  refAgeS: number | null;
  /** iOS: which axis the compass was found to follow (null: not known yet) */
  compassAxis: "top" | "camera" | null;
}

export interface OrientationReading {
  heading: number | null;
  /** the unfused compass reading as a camera heading */
  raw: number | null;
  accuracy: number | null;
  disturbed: boolean;
  note: string | null;
  details: SensorDetails;
}

function circMean(a: number, b: number): number {
  return norm360(a + wrap180(b - a) / 2);
}

/** The camera heading from DeviceOrientation (+ iOS webkitCompassHeading).
 * Pure: heading.ts feeds it the events. See the section comment above. */
export class OrientationFusion {
  /** true north minus the attitude frame's north (attitude mode) */
  readonly off = new HeadingFilter().asOffset();
  /** fallback without an attitude stream: the camera heading, gyro-fused (rotationRate) */
  readonly cam = new HeadingFilter();
  private relR: M3 | null = null;
  private relT = -Infinity;
  private camRel: number | null = null;
  ref: CompassRef = "none";
  private usedT: number | null = null;
  private used = false;
  private why: string | null = null;
  private compassCam: number | null = null;
  private acc: number | null = null;
  private source = "none";
  private pr: { pitch: number; roll: number } | null = null;
  private screen = 0;
  /** which axis this phone's webkitCompassHeading follows (learned) */
  readonly model = new WebkitModel();

  /** An attitude reading: iOS deviceorientation (alpha relative), Android's relative deviceorientation. */
  relative(t: number, alpha: number, beta: number, gamma: number, screenAngle = 0): void {
    if (![alpha, beta, gamma].every(Number.isFinite)) return;
    // the page was hidden / events stopped: iOS may have started a new frame
    if (t - this.relT > FUSION.FRAME_GAP_S && this.ref === "locked") this.ref = "provisional";
    const R = rotationFromEuler(alpha, beta, gamma);
    this.relR = R;
    this.relT = t;
    const c = cameraAzimuth(R);
    this.camRel = c.azimuth !== null && c.horiz >= 0.05 ? c.azimuth : null;
    this.screen = screenAngle;
    this.pr = cameraPitchRoll(R, screenAngle);
  }

  /** iOS: one deviceorientation event (relative alpha/beta/gamma + webkitCompassHeading, magnetic). */
  webkit(t: number, alpha: number | null, beta: number | null, gamma: number | null, webkitHeading: number | null,
    accuracy: number | null, declination: number, screenAngle = 0): void {
    this.source = "iOS compass + attitude";
    const usableAcc = accuracy === null || (accuracy >= 0 && accuracy <= FUSION.MAX_ACC_DEG);
    this.acc = accuracy !== null && accuracy >= 0 ? accuracy : null;
    const hasWk = webkitHeading !== null && Number.isFinite(webkitHeading);
    if (alpha === null || beta === null || gamma === null) {
      // no attitude at all: the old way (heading = compass + screen angle)
      this.source = "iOS compass only";
      if (hasWk) {
        const c = norm360((webkitHeading as number) + declination + screenAngle);
        this.compassCam = c;
        this.cam.compass(t, c, this.acc);
        this.used = true;
        this.usedT = t;
      }
      return;
    }
    this.relative(t, alpha, beta, gamma, screenAngle);
    if (!hasWk) {
      this.used = false;
      this.why = "no compass reading";
      return;
    }
    const R = this.relR as M3;
    const trueTop = norm360((webkitHeading as number) + declination);
    const tr = webkitTrust(R);
    // the compass as a camera heading, if iOS references the top edge
    this.compassCam = tr.top !== null && this.camRel !== null ? norm360(trueTop + wrap180(this.camRel - tr.top)) : null;
    if (!usableAcc) {
      this.used = false;
      this.why = `compass accuracy ±${Math.round(accuracy ?? 0)}°: not used`;
      return;
    }
    // learn which axis the compass follows (slope of the top-edge offset vs the top - camera difference)
    if (tr.diff !== null && Math.abs(tr.diff) <= FUSION.MODEL_MAX_DIFF_DEG && this.off.h !== null && this.ref === "locked") {
      this.model.add(tr.diff, wrap180(trueTop - (tr.top as number) - this.off.h));
    }
    const m = this.model.model;
    let sample: number | null = null;
    if (tr.ok) {
      // top and camera agree here: whichever axis iOS references, the sample is right
      // (the mean until the model is known: off by at most half the small difference)
      sample = norm360(trueTop - (m === "top" ? (tr.top as number) : m === "camera" ? (tr.cam as number) : circMean(tr.top as number, tr.cam as number)));
    } else if (m === "top" && tr.diff !== null && Math.abs(tr.diff) <= FUSION.MODEL_MAX_DIFF_DEG) {
      sample = norm360(trueTop - (tr.top as number));
    } else if (m === "camera" && tr.cam !== null && tr.camHoriz >= FUSION.TRUST_MIN_HORIZ
      && (tr.topHoriz < FUSION.TRUST_MIN_HORIZ || (tr.diff !== null && Math.abs(tr.diff) <= FUSION.MODEL_MAX_DIFF_DEG))) {
      sample = norm360(trueTop - tr.cam);
    }
    if (sample !== null) {
      if (this.ref !== "locked" || this.off.h === null) this.off.reset(t, sample);
      else this.off.compass(t, sample, this.acc);
      this.ref = "locked";
      this.used = true;
      this.usedT = t;
      this.why = null;
      return;
    }
    this.used = false;
    this.why = tr.why;
    if (this.ref !== "locked" && this.camRel !== null) {
      // never seen a trustworthy pose yet: assume (as before 2026-10-05) the
      // compass + screen angle is the camera heading, provisionally
      this.off.compass(t, norm360(trueTop + screenAngle - this.camRel), this.acc);
      this.ref = "provisional";
      this.used = true;
      this.usedT = t;
      this.why = "provisional: tilt the phone down at the ground ahead for a second to settle the compass";
    }
  }

  /** Android: deviceorientationabsolute (alpha from true / magnetic north). */
  absolute(t: number, alpha: number, beta: number, gamma: number, screenAngle = 0): void {
    if (![alpha, beta, gamma].every(Number.isFinite)) return;
    const R = rotationFromEuler(alpha, beta, gamma);
    const c = cameraAzimuth(R);
    this.acc = null;
    if (!this.relR || t - this.relT > FUSION.FRAME_GAP_S) {
      this.pr = cameraPitchRoll(R, screenAngle);
      this.screen = screenAngle;
    }
    if (c.azimuth === null || c.horiz < FUSION.ABS_MIN_HORIZ) {
      this.used = false;
      this.why = "camera pointing straight down / up: direction held";
      return;
    }
    this.compassCam = c.azimuth;
    this.cam.compass(t, c.azimuth, null);
    const paired = this.relR !== null && t - this.relT <= FUSION.REL_STALE_S && this.camRel !== null;
    this.source = paired ? "Android compass + attitude" : "Android compass + gyro";
    if (paired) {
      this.off.compass(t, norm360(c.azimuth - (this.camRel as number)), null);
      this.ref = "absolute";
    } else if (this.ref === "none") this.ref = "absolute";
    this.used = true;
    this.usedT = t;
    this.why = null;
  }

  /** DeviceMotion rotationRate: only the fallback filter uses it (the attitude carries turns otherwise). */
  gyro(t: number, ra: number, rb: number, rg: number, beta: number, gamma: number): void {
    this.cam.gyro(t, ra, rb, rg, beta, gamma);
  }

  /** Does the attitude stream carry the heading right now? */
  attitudeActive(t: number): boolean {
    return this.relR !== null && t - this.relT <= FUSION.FRAME_GAP_S && this.off.h !== null && this.camRel !== null;
  }

  reading(t: number): OrientationReading {
    const att = this.attitudeActive(t);
    const f = att ? this.off : this.cam;
    const st = f.state();
    const heading = att ? norm360((this.camRel as number) + (this.off.h as number)) : this.cam.h;
    return {
      heading,
      raw: this.compassCam,
      accuracy: this.acc,
      disturbed: st.disturbed,
      note: st.note,
      details: {
        source: this.source,
        yaw: att ? "attitude" : this.cam.gyroState === "ok" ? "gyro" : "none",
        ref: this.ref,
        used: this.used,
        why: this.why,
        compass: this.compassCam,
        accuracy: this.acc,
        pitch: this.pr ? this.pr.pitch : null,
        roll: this.pr ? this.pr.roll : null,
        refAgeS: this.usedT === null ? null : Math.max(0, t - this.usedT),
        compassAxis: this.model.model,
      },
    };
  }

  /** the screen angle last seen (display) */
  get screenAngle(): number {
    return this.screen;
  }
}

// ---------------------------------------------------------------------------
// The amber bar
// ---------------------------------------------------------------------------

export const ANCHOR_GOOD_M = 2;
export const ANCHOR_WEAK_M = 4;

export interface AnchorInput {
  calibrated: boolean;
  /** "manhole", "the kerb" ... */
  label: string | null;
  /** 1-sigma position uncertainty from the filter */
  sigma: number;
  /** distance from the current position to the anchor asset / kerb point */
  anchorDistM: number | null;
  held: boolean;
  compassNote: string | null;
  /** 2026-10-05: where the DIRECTION comes from when the calibration fixed
   * the position only ("compass", "the road (Kapiti Road)"): the bar then
   * says "Position anchored to hydrant 4 m away · direction from compass" */
  direction?: string | null;
}

export interface AnchorStatus {
  level: "held" | "anchored" | "weak" | "drifting" | "gps";
  text: string;
  warn: string | null;
}

export function anchorStatus(i: AnchorInput): AnchorStatus {
  const sig = `±${i.sigma < 9.5 ? i.sigma.toFixed(1) : Math.round(i.sigma)} m`;
  const warn = i.compassNote;
  if (i.held) return { level: "held", text: `Position held (${sig}), only direction updates`, warn };
  if (!i.calibrated) return { level: "gps", text: `GPS + motion, ${sig}`, warn };
  const what = i.label ?? "the calibration";
  const where = i.anchorDistM !== null && Number.isFinite(i.anchorDistM) ? ` ${Math.round(i.anchorDistM)} m away` : "";
  const dir = i.direction ? ` · direction from ${i.direction}` : "";
  if (i.sigma <= ANCHOR_GOOD_M) {
    return { level: "anchored", text: i.direction ? `Position anchored to ${what}${where} (${sig})${dir}` : `Anchored to ${what}${where} (${sig})`, warn };
  }
  if (i.sigma <= ANCHOR_WEAK_M) return { level: "weak", text: `Anchor fading (${sig}): recalibrate soon${dir}`, warn };
  return { level: "drifting", text: `Drifting (${sig}): recalibrate${dir}`, warn };
}

/** The 1-sigma position error a calibration leaves: the tap's ground-range
 * error (pitch) and the register position of the asset. */
export function calibrationSigmaM(assetDistM: number): number {
  return Math.max(0.7, 0.08 * Math.max(assetDistM, 0));
}
